import type { BookingAdditionalType, PreOrderItem } from "../../common/jsonRpcTypes";

/**
 * A single booking type of a linked booking, with the pre-order the guest
 * placed against it. Used to show one section per booking type (e.g. Lunch
 * then Dinner) on the payment step and the confirmation page.
 */
export interface BookingTypeGroup {
  bookingTypeId: string;
  /** Display name, when Collins provided one for the booking type. */
  bookingTypeName?: string;
  /** Sitting time for the booking type (e.g. "13:00"). */
  time?: string;
  items: PreOrderItem[];
  /** Net total of the items, before service charge. */
  subtotal: number;
  serviceChargeAmount: number;
  /** Total including service charge. */
  total: number;
}

/** The subset of a booking-type basket the grouping reads. */
export interface BookingTypeBasketSource {
  bookingType: string;
  bookingTypeName?: string;
  items?: PreOrderItem[];
  /** Guest names the items are ordered for, indexed by `for_name_index`. */
  forNames?: string[];
  totalCost?: number;
  totalCostWithService?: number;
  serviceChargeAmount?: number;
  booking?: {
    type?: string;
    additional_types?: BookingAdditionalType[];
  };
}

interface BuildBookingTypeGroupsOptions {
  /** The booking's primary booking type, listed first. */
  mainBookingTypeId?: string;
  /** Display name for the primary booking type. */
  mainBookingTypeName?: string;
  /** Sitting time for the primary booking type. */
  mainBookingTime?: string;
}

const sumItemTotals = (items: PreOrderItem[]): number => items.reduce((total, item) => total + (item.total ?? 0), 0);

/** Attach the guest name each item was ordered for, when the basket provides one. */
function withForNames(items: PreOrderItem[], forNames: string[] | undefined): PreOrderItem[] {
  if (!forNames?.length) return items;
  return items.map((item) => ({ ...item, for_name: item.for_name ?? forNames[item.for_name_index ?? 0] }));
}

/** Collect the linked booking types declared on any of the baskets, keyed by id. */
function collectAdditionalTypes(baskets: BookingTypeBasketSource[]): Map<string, BookingAdditionalType> {
  const additionalTypes = new Map<string, BookingAdditionalType>();
  for (const basket of baskets) {
    for (const additionalType of basket.booking?.additional_types ?? []) {
      if (additionalType?.id && !additionalTypes.has(additionalType.id)) {
        additionalTypes.set(additionalType.id, additionalType);
      }
    }
  }
  return additionalTypes;
}

/** Order the groups so the main booking type leads, followed by the linked ones in Collins' order. */
function sortGroups(groups: BookingTypeGroup[], mainBookingTypeId: string | undefined, orderedIds: string[]): void {
  const position = (bookingTypeId: string): number => {
    if (bookingTypeId === mainBookingTypeId) return -1;
    const index = orderedIds.indexOf(bookingTypeId);
    return index === -1 ? orderedIds.length : index;
  };
  groups.sort((a, b) => position(a.bookingTypeId) - position(b.bookingTypeId));
}

/**
 * Build one group per booking type from the cached booking-type baskets.
 *
 * Only linked bookings are grouped: when a single booking type carries items the
 * result is empty, so callers keep rendering their existing flat summary. Names
 * and times come from the basket itself where present, otherwise from the
 * booking's `additional_types` (or the supplied main booking type details).
 */
export function buildBookingTypeGroups(
  baskets: BookingTypeBasketSource[],
  { mainBookingTypeId, mainBookingTypeName, mainBookingTime }: BuildBookingTypeGroupsOptions = {},
): BookingTypeGroup[] {
  if (!Array.isArray(baskets)) return [];

  const withItems = baskets.filter((basket) => (basket.items?.length ?? 0) > 0);
  if (withItems.length < 2) return [];

  const additionalTypes = collectAdditionalTypes(baskets);
  const mainTypeId = mainBookingTypeId ?? baskets.find((basket) => basket.booking?.type)?.booking?.type;

  const groups = withItems.map((basket) => {
    const additionalType = additionalTypes.get(basket.bookingType);
    const isMainType = basket.bookingType === mainTypeId;
    const items = withForNames(basket.items ?? [], basket.forNames);
    const subtotal = basket.totalCost ?? sumItemTotals(items);
    const serviceChargeAmount = basket.serviceChargeAmount ?? 0;

    return {
      bookingTypeId: basket.bookingType,
      bookingTypeName: basket.bookingTypeName ?? additionalType?.name ?? (isMainType ? mainBookingTypeName : undefined),
      time: additionalType?.time ?? (isMainType ? mainBookingTime : undefined),
      items,
      subtotal,
      serviceChargeAmount,
      total: basket.totalCostWithService ?? subtotal + serviceChargeAmount,
    };
  });

  sortGroups(groups, mainTypeId, [...additionalTypes.keys()]);
  return groups;
}
